import api from './api';

export interface ExpenseCategory {
  id: number;
  family_id: string;
  name: string;
  icon: string;
  color: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExpenseTransaction {
  id: number;
  family_id: string;
  category_id: number;
  amount: number;
  expense_date: string;
  notes: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  category_name?: string;
  category_icon?: string;
  category_color?: string;
  created_by_name?: string;
}

export interface CreateExpenseTransactionRequest {
  category_id: number;
  amount: number;
  expense_date: string;
  notes?: string;
}

export interface UpdateExpenseTransactionRequest {
  category_id?: number;
  amount?: number;
  expense_date?: string;
  notes?: string;
}

export interface CreateExpenseCategoryRequest {
  name: string;
  icon?: string;
  color?: string;
}

export interface UpdateExpenseCategoryRequest {
  name?: string;
  icon?: string;
  color?: string;
  is_active?: boolean;
}

export interface ExpenseStatistics {
  categoryStats: Array<{
    id: number;
    name: string;
    icon: string;
    color: string;
    total_amount: number;
    transaction_count: number;
  }>;
  totalExpense: number;
  monthlyStats: Array<{
    month: string;
    total_amount: number;
    transaction_count: number;
  }>;
}

export interface ExpenseTransactionsResponse {
  success: boolean;
  data: ExpenseTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ExpenseCategoriesResponse {
  success: boolean;
  data: ExpenseCategory[];
}

export interface ExpenseStatisticsResponse {
  success: boolean;
  data: ExpenseStatistics;
}

// Utility function to format currency
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
  }).format(amount);
};

// ===== EXPENSE CATEGORIES =====

export const getExpenseCategories = async (): Promise<ExpenseCategory[]> => {
  try {
    const response = await api.get<ExpenseCategoriesResponse>('/api/expenses/categories');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching expense categories:', error);
    throw error;
  }
};

export const createExpenseCategory = async (data: CreateExpenseCategoryRequest): Promise<ExpenseCategory> => {
  try {
    const response = await api.post<{ success: boolean; data: ExpenseCategory }>('/api/expenses/categories', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating expense category:', error);
    throw error;
  }
};

export const updateExpenseCategory = async (id: number, data: UpdateExpenseCategoryRequest): Promise<ExpenseCategory> => {
  try {
    const response = await api.put<{ success: boolean; data: ExpenseCategory }>(`/api/expenses/categories/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating expense category:', error);
    throw error;
  }
};

export const deleteExpenseCategory = async (id: number): Promise<void> => {
  try {
    await api.delete(`/api/expenses/categories/${id}`);
  } catch (error) {
    console.error('Error deleting expense category:', error);
    throw error;
  }
};

// ===== EXPENSE TRANSACTIONS =====

export const getExpenseTransactions = async (params?: {
  page?: number;
  limit?: number;
  category_id?: number;
  start_date?: string;
  end_date?: string;
  created_by?: number;
  search?: string;
}): Promise<ExpenseTransactionsResponse> => {
  try {
    const response = await api.get<ExpenseTransactionsResponse>('/api/expenses/transactions', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching expense transactions:', error);
    throw error;
  }
};

export const createExpenseTransaction = async (data: CreateExpenseTransactionRequest): Promise<ExpenseTransaction> => {
  try {
    const response = await api.post<{ success: boolean; data: ExpenseTransaction }>('/api/expenses/transactions', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating expense transaction:', error);
    throw error;
  }
};

export const updateExpenseTransaction = async (id: number, data: UpdateExpenseTransactionRequest): Promise<ExpenseTransaction> => {
  try {
    const response = await api.put<{ success: boolean; data: ExpenseTransaction }>(`/api/expenses/transactions/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating expense transaction:', error);
    throw error;
  }
};

export const deleteExpenseTransaction = async (id: number): Promise<void> => {
  try {
    await api.delete(`/api/expenses/transactions/${id}`);
  } catch (error) {
    console.error('Error deleting expense transaction:', error);
    throw error;
  }
};

// ===== EXPENSE STATISTICS =====

export const getExpenseStatistics = async (params?: {
  start_date?: string;
  end_date?: string;
}): Promise<ExpenseStatistics> => {
  try {
    const response = await api.get<ExpenseStatisticsResponse>('/api/expenses/statistics', { params });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching expense statistics:', error);
    throw error;
  }
};

// ===== WALLET LINKING =====

export interface WalletLink {
  id: number;
  expense_id: number;
  wallet_id: number;
  amount: number;
  created_at: string;
  wallet_name: string;
  wallet_source_name: string;
  wallet_source_icon: string;
  wallet_source_color: string;
}

export interface SourceWallet {
  id: number;
  family_id: string;
  source_id: number;
  name: string;
  current_balance: number;
  initial_balance: number;
  description: string | null;
  is_active: boolean;
  created_by: number;
  source_name: string;
  source_icon: string;
  source_color: string;
  created_at: string;
  updated_at: string;
}

export interface WalletBalance {
  id: number;
  name: string;
  current_balance: number;
  source_name: string;
  source_icon: string;
  source_color: string;
}

// Liên kết giao dịch chi tiêu với ví
export const linkExpenseToWallet = async (
  expenseId: number, 
  walletId: number, 
  amount: number
): Promise<WalletLink> => {
  try {
    const response = await api.post<{ success: boolean; data: WalletLink }>(
      `/api/expenses/transactions/${expenseId}/link-wallet`,
      { wallet_id: walletId, amount }
    );
    return response.data.data;
  } catch (error) {
    console.error('Error linking expense to wallet:', error);
    throw error;
  }
};

// Lấy danh sách liên kết ví của một giao dịch chi tiêu
export const getExpenseWalletLinks = async (expenseId: number): Promise<WalletLink[]> => {
  try {
    const response = await api.get<{ success: boolean; data: WalletLink[] }>(
      `/api/expenses/transactions/${expenseId}/wallet-links`
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching expense wallet links:', error);
    throw error;
  }
};

// Xóa liên kết ví của một giao dịch chi tiêu
export const deleteExpenseWalletLink = async (expenseId: number, linkId: number): Promise<void> => {
  try {
    await api.delete(`/api/expenses/transactions/${expenseId}/wallet-links/${linkId}`);
  } catch (error) {
    console.error('Error deleting expense wallet link:', error);
    throw error;
  }
};

// Lấy danh sách ví có sẵn
export const getAvailableWallets = async (): Promise<SourceWallet[]> => {
  try {
    const response = await api.get<{ success: boolean; data: SourceWallet[] }>('/api/income/wallets');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching available wallets:', error);
    throw error;
  }
};

// Lấy số dư hiện tại của tất cả ví
export const getWalletBalances = async (): Promise<WalletBalance[]> => {
  try {
    const response = await api.get<{ success: boolean; data: WalletBalance[] }>('/api/expenses/wallet-balance');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching wallet balances:', error);
    throw error;
  }
};